import { CompraRapida } from "@/components/CompraRapida";
import { CompraRapidaGiro } from "@/components/CompraRapidaGiro";
import { Button } from "@/components/ui/button";
import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
} from "@/components/ui/carousel";
import { BuscarSorteioVigenteDto, SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import { cn } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";

interface HomeSorteioCarouselProps {
    sorteioPrincipal: BuscarSorteioVigenteDto;
    sorteioGiro: SorteioGiroDto;
}

const AUTOPLAY_DELAY_MS = 5000;
const RESUME_AFTER_MS = 8000;

const SLIDES = [
    { id: 0, label: "Principal" },
    { id: 1, label: "Giro" },
] as const;

export function HomeSorteioCarousel({
    sorteioPrincipal,
    sorteioGiro,
}: HomeSorteioCarouselProps) {
    const [api, setApi] = useState<CarouselApi>();
    const [slideAtivo, setSlideAtivo] = useState(0);
    const [alturaSlide, setAlturaSlide] = useState<number>();
    const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const autoplayPlugin = useRef(
        Autoplay({
            delay: AUTOPLAY_DELAY_MS,
            stopOnInteraction: true,
        })
    );

    const pausarEAgendarRetomada = useCallback(() => {
        const autoplay = api?.plugins()?.autoplay as
            | { stop: () => void; play: () => void }
            | undefined;
        if (!autoplay) {
            return;
        }

        autoplay.stop();

        if (resumeTimeoutRef.current) {
            clearTimeout(resumeTimeoutRef.current);
        }

        resumeTimeoutRef.current = setTimeout(() => {
            autoplay.play();
        }, RESUME_AFTER_MS);
    }, [api]);

    const atualizarAlturaDoSlideAtivo = useCallback(() => {
        if (!api) {
            return;
        }

        const slideNode = api.slideNodes()[api.selectedScrollSnap()];
        if (!slideNode) {
            return;
        }

        setAlturaSlide(slideNode.offsetHeight);
    }, [api]);

    const irParaSlide = useCallback(
        (index: number) => {
            api?.scrollTo(index);
            pausarEAgendarRetomada();
        },
        [api, pausarEAgendarRetomada]
    );

    const slideAnterior = useCallback(() => {
        api?.scrollPrev();
        pausarEAgendarRetomada();
    }, [api, pausarEAgendarRetomada]);

    const proximoSlide = useCallback(() => {
        api?.scrollNext();
        pausarEAgendarRetomada();
    }, [api, pausarEAgendarRetomada]);

    useEffect(() => {
        if (!api) {
            return;
        }

        const onSelect = () => {
            setSlideAtivo(api.selectedScrollSnap());
            requestAnimationFrame(() => atualizarAlturaDoSlideAtivo());
        };

        const onPointerDown = () => {
            pausarEAgendarRetomada();
        };

        onSelect();
        atualizarAlturaDoSlideAtivo();

        api.on("select", onSelect);
        api.on("reInit", atualizarAlturaDoSlideAtivo);
        api.on("settle", atualizarAlturaDoSlideAtivo);
        api.on("pointerDown", onPointerDown);
        window.addEventListener("resize", atualizarAlturaDoSlideAtivo);

        const imagens = api.rootNode()?.querySelectorAll("img") ?? [];
        imagens.forEach((img) => img.addEventListener("load", atualizarAlturaDoSlideAtivo));

        const resizeObserver = new ResizeObserver(() => {
            atualizarAlturaDoSlideAtivo();
        });
        api.slideNodes().forEach((node) => resizeObserver.observe(node));

        return () => {
            api.off("select", onSelect);
            api.off("reInit", atualizarAlturaDoSlideAtivo);
            api.off("settle", atualizarAlturaDoSlideAtivo);
            api.off("pointerDown", onPointerDown);
            window.removeEventListener("resize", atualizarAlturaDoSlideAtivo);
            imagens.forEach((img) => img.removeEventListener("load", atualizarAlturaDoSlideAtivo));
            resizeObserver.disconnect();
            if (resumeTimeoutRef.current) {
                clearTimeout(resumeTimeoutRef.current);
            }
        };
    }, [api, atualizarAlturaDoSlideAtivo, pausarEAgendarRetomada]);

    return (
        <div className="flex flex-col w-full px-5">
            <div className="flex items-end gap-1">
                {SLIDES.map((slide) => {
                    const ativo = slideAtivo === slide.id;
                    return (
                        <button
                            key={slide.id}
                            type="button"
                            onClick={() => irParaSlide(slide.id)}
                            className={cn(
                                "relative z-10 min-w-[7.5rem] rounded-t-lg border-2 border-b-0 px-4 py-2 text-sm font-semibold transition-colors",
                                ativo
                                    ? "-mb-[2px] border-yellow-400 bg-yellow-400 text-foreground"
                                    : "border-transparent bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground"
                            )}
                            aria-pressed={ativo}
                        >
                            {slide.label}
                        </button>
                    );
                })}
            </div>

            <div className="relative w-full overflow-hidden rounded-b-xl rounded-tr-xl border-2 border-yellow-400 bg-background pb-3 pt-2 transition-[height] duration-300 ease-out">
                <div
                    className="relative overflow-hidden transition-[height] duration-300 ease-out"
                    style={alturaSlide ? { height: alturaSlide } : undefined}
                >
                    <Carousel
                        setApi={setApi}
                        opts={{ loop: true }}
                        plugins={[autoplayPlugin.current]}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-0 items-start">
                            <CarouselItem className="pl-0 basis-full">
                                <div className="flex flex-col">
                                    <div className="md:hidden m-4 flex flex-col justify-center items-center">
                                        <img
                                            className="rounded-[5%]"
                                            src={sorteioPrincipal.ImagemPremioApp || sorteioPrincipal.ImagemPremio}
                                            alt={`Sorteio edição ${sorteioPrincipal.Edicao}`}
                                        />
                                    </div>

                                    <div className="hidden m-4 md:flex flex-col justify-center items-center">
                                        <NavLink to="/compra/titulos">
                                            <img
                                                className="rounded-lg"
                                                src={sorteioPrincipal.ImagemPremio}
                                                alt={`Sorteio edição ${sorteioPrincipal.Edicao}`}
                                            />
                                        </NavLink>
                                    </div>

                                    <CompraRapida onInteract={pausarEAgendarRetomada} />
                                </div>
                            </CarouselItem>

                            <CarouselItem className="pl-0 basis-full">
                                <div className="flex flex-col">
                                    <div className="md:hidden m-4 flex flex-col justify-center items-center">
                                        {sorteioGiro.ImagemPremioApp || sorteioGiro.ImagemPremio ? (
                                            <img
                                                className="rounded-[5%]"
                                                src={sorteioGiro.ImagemPremioApp || sorteioGiro.ImagemPremio}
                                                alt={`Sorteio Giro edição ${sorteioGiro.Edicao}`}
                                            />
                                        ) : (
                                            <div className="w-full h-48 rounded-[5%] bg-muted flex items-center justify-center text-muted-foreground">
                                                Sorteio Giro
                                            </div>
                                        )}
                                    </div>

                                    <div className="hidden m-4 md:flex flex-col justify-center items-center">
                                        {sorteioGiro.ImagemPremio ? (
                                            <img
                                                className="rounded-lg"
                                                src={sorteioGiro.ImagemPremio}
                                                alt={`Sorteio Giro edição ${sorteioGiro.Edicao}`}
                                            />
                                        ) : (
                                            <div className="w-full max-w-3xl h-64 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                                                Sorteio Giro
                                            </div>
                                        )}
                                    </div>

                                    <CompraRapidaGiro
                                        sorteioGiro={sorteioGiro}
                                        onInteract={pausarEAgendarRetomada}
                                    />
                                </div>
                            </CarouselItem>
                        </CarouselContent>
                    </Carousel>

                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="absolute left-1 top-[30%] z-10 h-10 w-10 -translate-y-1/2 rounded-full border-yellow-400/60 bg-background/90 shadow-md md:left-3"
                        onClick={slideAnterior}
                        aria-label="Slide anterior"
                    >
                        <ChevronLeft className="h-5 w-5 text-foreground" />
                    </Button>

                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="absolute right-1 top-[30%] z-10 h-10 w-10 -translate-y-1/2 rounded-full border-yellow-400/60 bg-background/90 shadow-md md:right-3"
                        onClick={proximoSlide}
                        aria-label="Próximo slide"
                    >
                        <ChevronRight className="h-5 w-5 text-foreground" />
                    </Button>
                </div>
            </div>

            <div className="mt-3 mb-1 flex flex-col items-center gap-2">
                <div className="flex justify-center gap-2" role="tablist" aria-label="Slides do sorteio">
                    {SLIDES.map((slide) => (
                        <button
                            key={slide.id}
                            type="button"
                            role="tab"
                            aria-selected={slideAtivo === slide.id}
                            aria-label={slide.label}
                            className={cn(
                                "h-2.5 rounded-full transition-all",
                                slideAtivo === slide.id
                                    ? "w-6 bg-yellow-400"
                                    : "w-2.5 bg-yellow-400/30 hover:bg-yellow-400/50"
                            )}
                            onClick={() => irParaSlide(slide.id)}
                        />
                    ))}
                </div>
                <p className="text-xs text-muted-foreground">
                    Deslize ou use as setas para ver o outro sorteio
                </p>
            </div>
        </div>
    );
}
